import { CircleDot } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { InputOption } from "@/shared/types";
import type { HealthHabitOption, QuestionItem } from "@/shared/types/healthFlowType";
import {
  HEALTH_ANNUAL_INCOME_RANGE_OPTIONS,
  HEALTH_HABIT_OPTIONS,
  HEALTH_MEDICAL_CONDITION_OPTIONS,
  HEALTH_OCCUPATION_TYPE_OPTIONS,
  HEALTH_QUESTIONS,
  HEALTH_QUESTION_LABELS,
  HEALTH_WORK_PROFILE_OPTIONS,
} from "../common/static";
import type {
  HealthAnnualIncomeRange,
  HealthHabitKey,
  HealthMedicalConditionKey,
  HealthOccupationType,
  HealthWorkProfile,
} from "../redux/type";
import { fetchHealthMasterData } from "../services/masterData";

type QuestionLabels = {
  medicalConditions: { title: string; description: string };
  lifestyle: { title: string; description: string };
  additionalInformation: { title: string; description: string };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const toNonEmptyString = (value: unknown) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const toOptionArray = (value: unknown): InputOption<string>[] | null => {
  if (!Array.isArray(value)) return null;

  const options = value
    .map((item) => {
      if (!isRecord(item)) return null;

      const label =
        toNonEmptyString(item.label) || toNonEmptyString(item.name) || toNonEmptyString(item.title);
      const rawValue = item.value ?? item.key ?? item.code ?? item.id;
      const optionValue = toNonEmptyString(rawValue);

      if (!label || !optionValue) return null;

      return {
        label,
        value: optionValue,
      };
    })
    .filter(Boolean) as InputOption<string>[];

  return options.length > 0 ? options : null;
};

const toHabitOptions = (value: unknown): HealthHabitOption[] | null => {
  if (!Array.isArray(value)) return null;

  const iconMap = HEALTH_HABIT_OPTIONS.reduce<Record<string, HealthHabitOption["icon"]>>(
    (acc, item) => {
      acc[item.key] = item.icon;
      return acc;
    },
    {}
  );

  const habits = value
    .map((item) => {
      if (!isRecord(item)) return null;

      const key =
        toNonEmptyString(item.key) || toNonEmptyString(item.value) || toNonEmptyString(item.code);
      const label =
        toNonEmptyString(item.label) || toNonEmptyString(item.name) || toNonEmptyString(item.title);
      const description = toNonEmptyString(item.description);

      if (!key || !label) return null;

      return {
        key: key as HealthHabitKey,
        label,
        description: description || undefined,
        icon: iconMap[key] || CircleDot,
      };
    })
    .filter(Boolean) as HealthHabitOption[];

  return habits.length > 0 ? habits : null;
};

const toQuestionItems = (value: unknown): QuestionItem[] | null => {
  if (!Array.isArray(value)) return null;

  const questions = value
    .map((item) => {
      if (!isRecord(item)) return null;

      const key =
        toNonEmptyString(item.key) || toNonEmptyString(item.value) || toNonEmptyString(item.code);
      const question =
        toNonEmptyString(item.question) ||
        toNonEmptyString(item.label) ||
        toNonEmptyString(item.title);

      if (!key || !question) return null;

      return {
        key: key as QuestionItem["key"],
        question,
      };
    })
    .filter(Boolean) as QuestionItem[];

  return questions.length > 0 ? questions : null;
};

const toQuestionLabels = (value: unknown): QuestionLabels | null => {
  if (!isRecord(value)) return null;

  const readBlock = (source: unknown, fallback: { title: string; description: string }) => {
    if (!isRecord(source)) return fallback;

    const title = toNonEmptyString(source.title) || fallback.title;
    const description = toNonEmptyString(source.description) || fallback.description;

    return {
      title,
      description,
    };
  };

  return {
    medicalConditions: readBlock(value.medicalConditions, HEALTH_QUESTION_LABELS.medicalConditions),
    lifestyle: readBlock(value.lifestyle, HEALTH_QUESTION_LABELS.lifestyle),
    additionalInformation: readBlock(
      value.additionalInformation,
      HEALTH_QUESTION_LABELS.additionalInformation
    ),
  };
};

export function useHealthMasterData() {
  const [remoteData, setRemoteData] = useState<unknown>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      const payload = await fetchHealthMasterData();
      if (!active || !payload) return;
      setRemoteData(payload);
    };

    void load();

    return () => {
      active = false;
    };
  }, []);

  return useMemo(() => {
    const source = isRecord(remoteData) ? remoteData : {};

    const healthHabitOptions =
      toHabitOptions(source.habits ?? source.healthHabits ?? source.health_habits) ||
      HEALTH_HABIT_OPTIONS;

    const healthQuestions =
      toQuestionItems(source.questions ?? source.healthQuestions ?? source.health_questions) ||
      HEALTH_QUESTIONS;

    const healthMedicalConditionOptions =
      toOptionArray(
        source.medicalConditions ??
          source.healthMedicalConditions ??
          source.health_medical_conditions
      )?.map((item) => ({ key: item.value as HealthMedicalConditionKey, label: item.label })) ||
      HEALTH_MEDICAL_CONDITION_OPTIONS;

    const healthOccupationTypeOptions =
      (toOptionArray(
        source.occupationTypes ?? source.healthOccupationTypes ?? source.health_occupation_types
      ) as InputOption<HealthOccupationType>[] | null) || HEALTH_OCCUPATION_TYPE_OPTIONS;

    const healthWorkProfileOptions =
      (toOptionArray(
        source.workProfiles ?? source.healthWorkProfiles ?? source.health_work_profiles
      ) as InputOption<HealthWorkProfile>[] | null) || HEALTH_WORK_PROFILE_OPTIONS;

    const healthAnnualIncomeRangeOptions =
      (toOptionArray(
        source.annualIncomeRanges ??
          source.healthAnnualIncomeRanges ??
          source.health_annual_income_ranges
      ) as InputOption<HealthAnnualIncomeRange>[] | null) || HEALTH_ANNUAL_INCOME_RANGE_OPTIONS;

    const healthQuestionLabels =
      toQuestionLabels(source.questionLabels ?? source.healthQuestionLabels ?? source.labels) ||
      HEALTH_QUESTION_LABELS;

    return {
      healthHabitOptions,
      healthQuestions,
      healthMedicalConditionOptions,
      healthOccupationTypeOptions,
      healthWorkProfileOptions,
      healthAnnualIncomeRangeOptions,
      healthQuestionLabels,
    };
  }, [remoteData]);
}
